import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faLink, faLinkSlash, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import CustomTooltip from "../../../../Elements/Tooltip";
import CookieBlockedMessage from "../../../../CommonComponents/CookieBlockedMessage";
import { shouldShowEmbeddedContent } from "../../../../../utils/cookieConsentUtils";

const FIXED_W = 220;
const FIXED_H = 104;
const FIT_W = 300;
const FIT_H = 140;

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const PreviewSizeBlock = ({ label, fitBox = false, box, icon, src, file, tone = 'light', fill = null, opacity = 1, contentTransform, embedSrc, embedKind = 'video', hideCanvas = false, onPickFile, pickHint, width, height, lock, presets = [], fit, error, children }) => {
    const [fileUrl, setFileUrl] = useState(null);

    useEffect(() => {
        if (!(file instanceof Blob)) {
            setFileUrl(null);
            return;
        }
        const url = URL.createObjectURL(file);
        setFileUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const [embedAllowed, setEmbedAllowed] = useState(shouldShowEmbeddedContent);

    useEffect(() => {
        const sync = () => setEmbedAllowed(shouldShowEmbeddedContent());
        window.addEventListener('cookieConsentChanged', sync);
        return () => window.removeEventListener('cookieConsentChanged', sync);
    }, []);

    const imageSrc = fileUrl || src || null;
    const boxW = num(box?.width);
    const boxH = num(box?.height);
    const fitScale = fitBox && boxW > 0 && boxH > 0 ? Math.min(FIT_W / boxW, FIT_H / boxH) : 0;
    const bw = fitBox ? boxW * fitScale : FIXED_W;
    const bh = fitBox ? boxH * fitScale : FIXED_H;

    const stage = (
        <div className="pv-stage" style={{ width: bw, height: bh }}>
            {bw > 0 && bh > 0 && (
                <div
                    className={`pv-box pv-box--${imageSrc ? 'image' : fill ? 'fill' : tone}`}
                    style={{ left: 0, top: 0, width: bw, height: bh, ...(fill && (imageSrc ? { background: fill } : { background: fill, opacity })), ...(contentTransform && { transform: contentTransform }) }}
                >
                    {embedSrc ? (
                        embedAllowed ? (
                            <iframe className="pv-embed" src={embedSrc} title={embedKind === 'video' ? 'Video preview' : 'Embed preview'} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
                        ) : (
                            <CookieBlockedMessage style={{ width: bw, height: bh }} contentType={embedKind} sourceUrl={embedSrc} />
                        )
                    ) : imageSrc ? <img src={imageSrc} alt="" style={{ opacity }} /> : !fill && icon && bw >= 28 && bh >= 20 && <FontAwesomeIcon icon={icon} />}
                </div>
            )}
        </div>
    );

    return (
        <div className={`pv-block${hideCanvas ? ' pv-block--no-canvas' : ''}`}>
            {hideCanvas ? null : onPickFile ? (
                <label className="pv-canvas pv-canvas--pick" title={imageSrc ? 'Choose a different image' : 'Choose an image'}>
                    <span className="pv-tag">{label}</span>
                    <input type="file" accept="image/*" onChange={onPickFile} />
                    {stage}
                    <span className="pv-pick-hint">{pickHint || (imageSrc ? 'Click to replace image' : 'Click to choose an image')}</span>
                </label>
            ) : (
                <div className="pv-canvas">
                    <span className="pv-tag">{label}</span>
                    {stage}
                </div>
            )}
            <div className="pv-size">
                <span className="pv-label">Size</span>
                <div className="pv-dims">
                    <label className="pv-dim-label" htmlFor={`${lock?.id}-w`}>W</label>
                    <input id={`${lock?.id}-w`} type="number" min="1" {...width} onFocus={(e) => e.target.select()} />
                    <CustomTooltip
                        placement="right"
                        title={
                            <p className="p-1 text-light mb-0">
                                {lock?.checked
                                    ? 'Proportions locked: changing the width also changes the height, and the other way round. Click to unlock.'
                                    : 'Proportions unlocked: width and height change independently. Click to lock them together.'}
                            </p>
                        }
                    >
                        <label className={`pv-lock${lock?.checked ? ' on' : ''}`}>
                            <input type="checkbox" {...lock} checked={!!lock?.checked} />
                            <FontAwesomeIcon icon={lock?.checked ? faLink : faLinkSlash} />
                            <span className="visually-hidden">Keep proportions</span>
                        </label>
                    </CustomTooltip>
                    <label className="pv-dim-label pv-dim-h" htmlFor={`${lock?.id}-h`}>H</label>
                    <input className="pv-dim-h" id={`${lock?.id}-h`} type="number" min="1" {...height} onFocus={(e) => e.target.select()} />
                </div>
                {presets.length > 0 && (
                    <div className="pv-presets">
                        {presets.map((p) => (
                            <button key={p.label} type="button" onClick={p.onClick}>{p.label}</button>
                        ))}
                    </div>
                )}
                {error ? (
                    <span className="pv-fit warn">{error}</span>
                ) : fit ? (
                    <span className={`pv-fit${fit.ok ? '' : ' warn'}`}>
                        <FontAwesomeIcon icon={fit.ok ? faCheck : faTriangleExclamation} /> {fit.text}
                    </span>
                ) : null}
            </div>
            {children && <div className="pv-extra">{children}</div>}
        </div>
    );
};

export default PreviewSizeBlock;
